// resources/js/Pages/HallOfFame/components/FilterBar.jsx
import React, { useState, useEffect, useRef } from 'react';

// Genre taxonomy color map - matches your PHP array
const GENRE_COLORS = {
    'Drama': '#2F3E7C',
    'Fantasy': '#7B3FE4',
    'Action': '#D81B37',
    'Thriller': '#0F172A',
    'Romance': '#E83E8C',
    'Science Fiction': '#0EA5E9',
    'Crime': '#0F766E',
    'Comedy': '#F59E0B',
    'Horror': '#7F1D1D',
    'War': '#6B7280',
    'Western': '#8B5E34',
};

export default function FilterBar({ 
    genres, 
    scriptTypes, 
    selectedGenres, 
    selectedScriptTypes, 
    onGenreChange, 
    onScriptTypeChange,
    onClearFilters,
    filteredCount,
    totalCount
}) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [isSticky, setIsSticky] = useState(false);
    const hasActiveFilters = selectedGenres.length > 0 || selectedScriptTypes.length > 0;
    const activeFilterCount = selectedGenres.length + selectedScriptTypes.length;
    
    // ADD: Ref for dynamic height tracking (kept for potential future use)
    const barRef = useRef(null);
    const [barHeight, setBarHeight] = useState(0);
    
    // Measure height whenever filters change (kept for potential future use)
    useEffect(() => {
        if (barRef.current && !isSticky) {
            setBarHeight(barRef.current.offsetHeight);
        }
    }, [selectedGenres, selectedScriptTypes, isSticky]);
    
    // Detect scroll to make sticky and auto-collapse
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            
            // 1. SHORT PAGE GUARD: 
            // If the page is too short (filtered results), disable sticky entirely 
            // to prevent the "jump loop" where the bar flickers on and off.
            const docHeight = document.documentElement.scrollHeight;
            const winHeight = window.innerHeight;
            
            if (docHeight < winHeight + 250) {
                setIsSticky(false);
                setIsExpanded(true); // Always keep open on short pages
                return; 
            }

            // 2. STICKY ACTIVATION WITH AUTO-COLLAPSE:
            // When scrolling past 150px, make sticky and auto-collapse
            if (currentScrollY > 150) {
                setIsSticky(true);
                setIsExpanded(false); // Auto-collapse when sticky
            } else {
                setIsSticky(false);
                setIsExpanded(true); // Auto-expand when back at the top
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []); // Empty dependency array is fine here
    
    const toggleGenre = (genre) => {
        if (selectedGenres.includes(genre)) {
            onGenreChange(selectedGenres.filter(g => g !== genre));
        } else {
            onGenreChange([...selectedGenres, genre]);
        }
    };
    
    const toggleScriptType = (type) => {
        if (selectedScriptTypes.includes(type)) {
            onScriptTypeChange(selectedScriptTypes.filter(t => t !== type));
        } else {
            onScriptTypeChange([...selectedScriptTypes, type]);
        }
    };
    
    return (
        <>
            {/* SAFE PLACEHOLDER: Always 55px. 
                This prevents the "Big Empty Space" and "Jumping". 
                If the menu expands while sticky, it will float OVER the table 
                instead of pushing the table down. */}
            {isSticky && (
                <div style={{ height: '55px' }} />
            )}
            
            <div 
                ref={barRef}
                className={`filter-chips-bar ${isSticky ? 'filter-sticky' : ''}`}
                style={{ 
                    background: 'white',
                    border: '1px solid rgba(8, 58, 84, 0.1)',
                    borderRadius: isSticky ? '0 0 8px 8px' : '8px',
                    boxShadow: isSticky ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.05)',
                    position: isSticky ? 'fixed' : 'relative',
                    top: isSticky ? 0 : 'auto',
                    left: isSticky ? 0 : 'auto',
                    right: isSticky ? 0 : 'auto',
                    zIndex: isSticky ? 1000 : 1,
                    marginBottom: isSticky ? 0 : '12px',
                    transition: 'box-shadow 0.3s ease',
                    maxWidth: isSticky ? '100%' : 'auto'
                }}
            >
            {/* Collapsed Sticky Header */}
            {isSticky && !isExpanded && (
                <div 
                    className="p-2 d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        setIsExpanded(true);
                    }}
                >
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#083A54' }}>
                            <i className="fa fa-filter me-2"></i>
                            FILTERS
                        </span>
                        
                        {/* Show script count */}
                        <span 
                            style={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: '#666',
                                background: '#f0f0f0',
                                padding: '2px 8px',
                                borderRadius: '10px'
                            }}
                        >
                            {filteredCount} {filteredCount === 1 ? 'script' : 'scripts'}
                            {hasActiveFilters && ` of ${totalCount}`}
                        </span>
                        
                        {/* Show selected genres as small chips */}
                        {selectedGenres.slice(0, 3).map(genre => (
                            <button
                                key={genre}
                                style={{
                                    background: GENRE_COLORS[genre] || '#6B7280',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGenre(genre);
                                }}
                            >
                                {genre} <span style={{ marginLeft: '4px' }}>×</span>
                            </button>
                        ))}
                        
                        {selectedGenres.length > 3 && (
                            <span style={{ fontSize: '0.7rem', color: '#666' }}>
                                +{selectedGenres.length - 3} more
                            </span>
                        )}
                        
                        {/* Show selected script types */}
                        {selectedScriptTypes.map(type => (
                            <button
                                key={type}
                                style={{
                                    background: '#23BAB1',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer'
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleScriptType(type);
                                }}
                            >
                                {type} <span style={{ marginLeft: '4px' }}>×</span>
                            </button>
                        ))}
                        
                        {!hasActiveFilters && (
                            <span style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic' }}>
                                Click to filter scripts
                            </span>
                        )}
                    </div>
                    
                    <div 
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#083A54',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '0.85rem'
                        }}
                    >
                        <i className="fa fa-chevron-down"></i>
                    </div>
                </div>
            )}
            
            {/* Expanded Filter Content */}
            {(!isSticky || isExpanded) && (
                <div className="p-3">
                    {/* Header with collapse button and count when sticky */}
                    {isSticky && (
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#083A54' }}>
                                    <i className="fa fa-filter me-2"></i>
                                    FILTERS
                                </span>
                                <span 
                                    style={{
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        color: '#666',
                                        background: '#f0f0f0',
                                        padding: '2px 8px',
                                        borderRadius: '10px'
                                    }}
                                >
                                    {filteredCount} {filteredCount === 1 ? 'script' : 'scripts'}
                                    {hasActiveFilters && ` of ${totalCount}`}
                                </span>
                            </div>
                            <button
                                onClick={() => setIsExpanded(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#083A54',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                }}
                            >
                                <i className="fa fa-chevron-up me-1"></i>
                                Collapse
                            </button>
                        </div>
                    )}
                    
                    {/* Show count in non-sticky mode too */}
                    {!isSticky && (
                        <div className="mb-3">
                            <span 
                                style={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: '#666'
                                }}
                            >
                                {/* Showing {filteredCount} of {totalCount} {totalCount === 1 ? 'script' : 'scripts'} */}
                            </span>
                        </div>
                    )}
                    
                    {/* Genre Filter Group */}
                    <div className="filter-group mb-3 pb-3" style={{ borderBottom: '1px solid rgba(8, 58, 84, 0.1)' }}>
                        <div 
                            className="filter-group-label mb-2 d-flex justify-content-between align-items-center"
                            style={{
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: '#083A54'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="fa fa-film" style={{ fontSize: '0.9rem', color: '#23BAB1' }}></i>
                                GENRE
                            </div>
                            {selectedGenres.length > 1 && (
                                <span 
                                    style={{
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        color: '#23BAB1',
                                        background: 'rgba(35, 186, 177, 0.1)',
                                        padding: '2px 8px',
                                        borderRadius: '10px'
                                    }}
                                >
                                    Must have ALL selected
                                </span>
                            )}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            {genres.map(genre => {
                                const isActive = selectedGenres.includes(genre);
                                const genreColor = GENRE_COLORS[genre] || '#6B7280';
                                
                                return (
                                    <button
                                        key={genre}
                                        onClick={() => toggleGenre(genre)}
                                        className="filter-chip-genre"
                                        style={{
                                            background: genreColor,
                                            border: `${isActive ? '3px' : '2px'} solid ${genreColor}`,
                                            color: 'white',
                                            padding: '8px 16px',
                                            borderRadius: '20px',
                                            fontSize: '0.875rem',
                                            fontWeight: isActive ? 700 : 500,
                                            cursor: 'pointer',
                                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                            transform: isActive ? 'scale(1.08)' : 'scale(0.92)',
                                            boxShadow: isActive 
                                                ? `0 0 20px ${genreColor}, 0 4px 12px rgba(0, 0, 0, 0.25)` 
                                                : '0 1px 3px rgba(0, 0, 0, 0.12)',
                                            position: 'relative',
                                            zIndex: isActive ? 10 : 1
                                        }}
                                    >
                                        {genre}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    
                    {/* Script Type Filter Group */}
                    <div className="filter-group mb-2">
                        <div 
                            className="filter-group-label mb-2"
                            style={{
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: '#083A54',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <i className="fa fa-file-text" style={{ fontSize: '0.9rem', color: '#23BAB1' }}></i>
                            SCRIPT TYPE
                        </div>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                            {scriptTypes.map(type => {
                                const isActive = selectedScriptTypes.includes(type);
                                
                                return (
                                    <button
                                        key={type}
                                        onClick={() => toggleScriptType(type)}
                                        className="filter-chip-type"
                                        style={{
                                            background: isActive ? '#23BAB1' : 'rgba(8, 58, 84, 0.06)',
                                            border: `${isActive ? '3px' : '2px'} solid ${isActive ? '#23BAB1' : 'rgba(8, 58, 84, 0.2)'}`,
                                            color: isActive ? 'white' : '#083A54',
                                            padding: '8px 16px',
                                            borderRadius: '20px',
                                            fontSize: '0.875rem',
                                            fontWeight: isActive ? 700 : 500,
                                            cursor: 'pointer',
                                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                            transform: isActive ? 'scale(1.08)' : 'scale(0.92)',
                                            boxShadow: isActive 
                                                ? '0 0 20px rgba(35, 186, 177, 0.5), 0 4px 12px rgba(0, 0, 0, 0.25)' 
                                                : '0 1px 3px rgba(0, 0, 0, 0.12)'
                                        }}
                                    >
                                        {type}
                                    </button>
                                );
                            })}
                            
                            {/* Clear Filters Button */}
                            {hasActiveFilters && (
                                <button 
                                    onClick={onClearFilters}
                                    style={{
                                        background: 'transparent',
                                        border: '2px dashed #dc3545',
                                        color: '#dc3545',
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        marginLeft: '8px'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#dc3545';
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.borderStyle = 'solid';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#dc3545';
                                        e.currentTarget.style.borderStyle = 'dashed';
                                    }}
                                >
                                    <i className="fa fa-times-circle"></i>
                                    Clear All Filters
                                    <span 
                                        style={{
                                            background: '#dc3545',
                                            color: 'white',
                                            borderRadius: '10px',
                                            padding: '2px 8px',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            marginLeft: '4px'
                                        }}
                                    >
                                        {activeFilterCount}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
}